import { useState, useRef, useEffect } from 'react';

interface StackedBarChartProps {
  data: { label: string; segments: { label: string; value: number; color: string }[] }[];
  height?: number;
  currency?: string;
}

export default function StackedBarChart({ data, height = 220, currency = '$' }: StackedBarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(500);
  const [hovered, setHovered] = useState<{ bar: number; seg: number } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    setContainerWidth(containerRef.current.clientWidth);
    return () => observer.disconnect();
  }, []);

  if (data.length === 0) return null;

  const maxVal = Math.max(...data.map(d => d.segments.reduce((s, seg) => s + seg.value, 0)), 1);
  const padding = { top: 24, bottom: 48, left: 50, right: 16 };
  const gap = 6;
  const availW = containerWidth - padding.left - padding.right;
  const barWidth = Math.max(20, Math.min(40, (availW / data.length) - gap));
  const needsScroll = data.length * (barWidth + gap) > availW;
  const svgWidth = needsScroll ? data.length * (barWidth + gap) + padding.left + padding.right : containerWidth;
  const chartH = height - padding.top - padding.bottom;

  function valToH(val: number) {
    return (val / maxVal) * chartH;
  }

  const gridLines = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div ref={containerRef} className="w-full overflow-x-auto">
      <svg width={svgWidth} height={height} viewBox={`0 0 ${svgWidth} ${height}`}>
        {gridLines.map(frac => {
          const y = padding.top + chartH - frac * chartH;
          const val = frac * maxVal;
          return (
            <g key={frac}>
              <line x1={padding.left} y1={y} x2={svgWidth - padding.right} y2={y} stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray={frac === 0 ? '0' : '4'} />
              <text x={padding.left - 6} y={y + 4} textAnchor="end" fill="rgba(255,255,255,0.7)" fontSize="10">
                {val >= 1000 ? `${(val / 1000).toFixed(1)}k` : val.toFixed(0)}
              </text>
            </g>
          );
        })}

        {data.map((bar, bi) => {
          const x = padding.left + 4 + bi * (barWidth + gap);
          let yOffset = 0;
          return (
            <g key={bi}>
              {bar.segments.map((seg, si) => {
                const h = valToH(seg.value);
                const y = padding.top + chartH - yOffset - h;
                yOffset += h;
                const isHov = hovered?.bar === bi && hovered?.seg === si;
                return (
                  <g
                    key={si}
                    onMouseEnter={() => setHovered({ bar: bi, seg: si })}
                    onMouseLeave={() => setHovered(null)}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect
                      x={x}
                      y={y}
                      width={barWidth}
                      height={Math.max(h, 1)}
                      rx={si === bar.segments.length - 1 ? 3 : 0}
                      fill={seg.color}
                      opacity={isHov ? 1 : 0.82}
                      style={{ transition: 'opacity 0.15s' }}
                    />
                    {isHov && (
                      <>
                        <rect x={x + barWidth / 2 - 36} y={y - 22} width={72} height={18} rx={4} fill="#1e293b" opacity="0.92" />
                        <text x={x + barWidth / 2} y={y - 9} textAnchor="middle" fill="#fff" fontSize="10" fontWeight="600">
                          {seg.label}: {currency}{(seg.value / 100).toFixed(0)}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}
              <text x={x + barWidth / 2} y={height - padding.bottom + 16} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="9">
                {bar.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
